export function tryParseGuid(value: string): string | null {
  const trimmed = value.trim();

  const dFormat = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const nFormat = /^[0-9a-f]{32}$/i;

  let candidate = trimmed;
  if (
    (candidate.startsWith('{') && candidate.endsWith('}')) ||
    (candidate.startsWith('(') && candidate.endsWith(')'))
  ) {
    candidate = candidate.substring(1, candidate.length - 1);
  }

  if (dFormat.test(candidate)) {
    return candidate.toLowerCase();
  }

  if (nFormat.test(candidate)) {
    const hex = candidate.toLowerCase();
    return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20)}`;
  }

  return null;
}

function byteToHex(byte: number): string {
  return byte.toString(16).padStart(2, '0');
}

export function base64ToGuid(base64: string): string {
  const parsedGuid = tryParseGuid(base64);
  if (parsedGuid !== null) {
    return parsedGuid;
  }

  const normalized = base64.replace(/_/g, '/').replace(/-/g, '+') + '==';

  const decoded = atob(normalized);
  if (decoded.length !== 16) {
    throw new Error(`Byte array for GUID must be exactly 16 bytes long. Was ${decoded.length}.`);
  }

  const bytes = Array.from(decoded, (character) => character.charCodeAt(0));

  const part1 = byteToHex(bytes[3]!) + byteToHex(bytes[2]!) + byteToHex(bytes[1]!) + byteToHex(bytes[0]!);
  const part2 = byteToHex(bytes[5]!) + byteToHex(bytes[4]!);
  const part3 = byteToHex(bytes[7]!) + byteToHex(bytes[6]!);
  const part4 = byteToHex(bytes[8]!) + byteToHex(bytes[9]!);
  const part5 = bytes.slice(10).map(byteToHex).join('');

  return `${part1}-${part2}-${part3}-${part4}-${part5}`;
}

export function guidToBase64(guid: string): string {
  const canonical = tryParseGuid(guid);
  if (canonical === null) {
    throw new Error(`"${guid}" is not a valid GUID.`);
  }

  const hex = canonical.replace(/-/g, '');
  const hexByte = (index: number): number => parseInt(hex.substring(index * 2, index * 2 + 2), 16);

  const bytes = [
    hexByte(3),
    hexByte(2),
    hexByte(1),
    hexByte(0),
    hexByte(5),
    hexByte(4),
    hexByte(7),
    hexByte(6),
    hexByte(8),
    hexByte(9),
    hexByte(10),
    hexByte(11),
    hexByte(12),
    hexByte(13),
    hexByte(14),
    hexByte(15),
  ];

  const base64 = btoa(String.fromCharCode(...bytes));
  return base64.replace(/\//g, '_').replace(/\+/g, '-').replace(/=+$/, '');
}
